import React, { Fragment, memo, useCallback, useMemo } from 'react'
import tw from 'twin.macro'
import { Form, Formik, FormikHelpers } from 'formik'
import { Button } from 'src/components/shared/Button'
import { CommonModal } from 'src/components/shared/CommonModal'
import { ModalHeader } from 'src/components/shared/ModalHeader'
import type { DeleteRemoteConfigFormValues, RemoteConfig } from 'src/interfaces/remoteConfig'
import { DeleteRemoteConfigFormContent } from './DeleteRemoteConfigFormContent'
import { buildDeleteRemoteConfigValidator } from './DeleteRemoteConfig.validator'

const FORM_ID = 'delete-remote-config-form'

/** `CommonModal` has no close-icon slot, so the header cancels the body padding to run edge to edge. */
const HEADER_CSS = tw`-mx-5 -mt-5 mb-5`

const INITIAL_VALUES: DeleteRemoteConfigFormValues = { confirmText: '' }

interface Props {
  isOpen: boolean
  remoteConfig: RemoteConfig | null
  isDeleting: boolean
  onConfirm(helpers: FormikHelpers<DeleteRemoteConfigFormValues>): void
  onClose(): void
}

const DeleteRemoteConfigModalComponent = ({
  isOpen,
  remoteConfig,
  isDeleting,
  onConfirm,
  onClose,
}: Props) => {
  const validationSchema = useMemo(() => {
    return buildDeleteRemoteConfigValidator(remoteConfig?.key ?? '')
  }, [remoteConfig])

  const handleSubmit = useCallback(
    (
      _values: DeleteRemoteConfigFormValues,
      helpers: FormikHelpers<DeleteRemoteConfigFormValues>,
    ) => {
      onConfirm(helpers)
    },
    [onConfirm],
  )

  if (!remoteConfig) {
    return null
  }

  return (
    <CommonModal
      isOpen={isOpen}
      className={tw`max-w-[480px]`}
      onClose={onClose}
      buttons={
        <Fragment>
          <Button size="small" color="grey" disabled={isDeleting} onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="small"
            color="red"
            type="submit"
            form={FORM_ID}
            loading={isDeleting}
            disabled={isDeleting}
            data-testid="remote-config-delete-confirm-button"
          >
            Delete key
          </Button>
        </Fragment>
      }
    >
      <ModalHeader
        title="Delete key"
        className={HEADER_CSS}
        onClose={onClose}
        closeButtonTestId="remote-config-delete-close-button"
      />
      <Formik<DeleteRemoteConfigFormValues>
        initialValues={INITIAL_VALUES}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        <Form id={FORM_ID} noValidate>
          <DeleteRemoteConfigFormContent remoteConfig={remoteConfig} />
        </Form>
      </Formik>
    </CommonModal>
  )
}

export const DeleteRemoteConfigModal = memo(DeleteRemoteConfigModalComponent)
